/**
 * sceneRegistry - maps a game location to a walkable Gaussian-splat scene.
 *
 * Current scope: six entries from the 90s NYC featured case (the MysHaps
 * agency, Torres Car Service, the Rivera/Casey apartment, the 90th Precinct
 * complaint desk, the Ingraham Street crime scene and the Morrison Towing
 * office), and all six locations of the Miami and noir featured cases. A window
 * backdrop is optional per scene: one entry may cut one window wall, another
 * several, another none at all, and a scene may set a flat background colour
 * for the sky past the backdrop's edges. A scene may also carry an `overlays` JSON URL,
 * which replaces smeared capture objects with crisp textured meshes, and a
 * `collision` block (a baked walk grid plus optional bounds/blockers) that keeps
 * the player out of walls and furniture. The NYC crime scene and most Miami
 * captures sit at the origin with the game camera at y = 0; a scene built in
 * metres from a Blender blockout (the other five NYC rooms, the Miami fuel dock
 * and marina slip, and every noir room) instead places its splat with `splat`,
 * raises the camera with `eyeHeight`, starts it at `start.pos` and may tighten
 * the near plane with `cameraNear`.
 * `url` is optional: a mesh-only room omits it and draws from `overlays` and/or
 * `meshRoom` (a baked room.json of meshes, lightmaps and lighting). `grayscale`
 * renders the room in black and white and `antialias` turns on MSAA.
 * Location ids are authored per case, so the most reliable key available to the
 * client is the location image path that the store already carries
 * (`currentImage`). A `locationMatch` on the location id is supported but unused
 * by the current entries, because their ids are generic and shared across every
 * featured case. Anything not listed falls back to the flat 2D location image.
 */

import { withSceneAssetBase } from './sceneAssets';

// Entries are module constants on purpose: SceneCanvas keys its scene effect on
// `windowBackdrop`/`collision` identity, so a per-call object would rebuild the
// room every render. Paths are authored same-origin (`/scenes/...`); `SCENES`
// below rewrites them onto REACT_APP_SCENE_BASE_URL once, at module load.
//
// `metersPerUnit` is the capture's real-world scale, which SceneCanvas uses to
// walk the same real speed in every room. In an origin capture the game camera
// sits at y = 0, the capture origin, so it is measured as the pano camera's
// eye height in the Blender blockout the room was painted from, over |floorY|
// of the scene's walk grid; Marble's `metric_scale_factor` and the blockout
// ceiling height over the splat's floor-to-ceiling span are the cross-checks.
// A metric scene (one with `splat`/`eyeHeight`) is authored in metres, so it
// is 1.0. A scene without it walks at Spark's fixed 1 unit/s.
const AUTHORED_SCENES = [
  // The NYC agency, Torres, the apartment, the precinct and Morrison Towing are
  // metric rooms (1 unit = 1 m, floor at y = 0), rebuilt from Blender
  // blockouts: a splat plate placed with `splat` plus crisp prop meshes from
  // `overlays`, in colour (no `grayscale`) with MSAA on (`antialias`). Values
  // mirror each room's archive viewer room.json and stage-6 wiring notes. The
  // files ship under a `v2/` folder beside the old origin captures, so a
  // deployed file is never overwritten. The collision bounds are the viewer's
  // room bounds; the grid (origin, cells) lives in walkgrid.json.
  {
    id: '90s_nyc/loc_myshaps',
    label: 'MysHaps Agency',
    url: '/scenes/90s_nyc/loc_myshaps/v2/scene_500k.spz',
    metersPerUnit: 1.0,
    // Matched against the current location image path.
    imageMatch: '/images/featured/90s_nyc/loc_myshaps',
    // No `locationMatch`: `myshaps` is an id shared by every featured case, so
    // matching on it would drop other cases into this capture.
    background: 0x101010,
    antialias: true,
    splat: { quaternion: [0.964586, 0.006085, -0.263609, 0.006916], scale: 1.097, position: [1.9363, 1.4069, 1.9107] },
    eyeHeight: 1.55,
    // Faces the desk and the window wall, with the corkboard on the right.
    start: { pos: [1.867, 1.903], yaw: 0.5335 },
    cameraNear: 0.05,
    // One cut wall, two openings onto the elevated-train street plate.
    windowBackdrop: [{
      image: '/scenes/90s_nyc/loc_myshaps/v2/street_wide.jpg',
      yaw: 0, distance: 3.21,
      plane: { width: 25, center: [-4.0, 0.53], depth: 9.79 },
      openings: [
        { s0: -2.302, s1: -1.11, y0: 0.872, y1: 2.508 },
        { s0: -0.802, s1: 0.822, y0: 0.86, y1: 2.48 },
      ],
      tint: 0xd9d9d9, softEdge: 0.01, rebuildFrames: false,
    }],
    overlays: '/scenes/90s_nyc/loc_myshaps/v2/overlays.json',
    collision: {
      grid: '/scenes/90s_nyc/loc_myshaps/v2/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-3.308, -3.222], max: [3.338, 3.222] },
    },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    examinePoints: [
      // Just above the desk top (y 0.7852), inside its footprint.
      { label: 'The desk', position: [1.445, 0.79, -0.175] },
      { label: 'Filing cabinets', position: [-2.357, 1.0, -1.104] },
      { label: 'Bookshelf', position: [1.795, 1.0, -2.637] },
      { label: 'Corkboard', position: [2.863, 1.6, 1.65] },
    ],
  },
  {
    id: '90s_nyc/loc_victim_workplace',
    label: 'Torres Car Service',
    url: '/scenes/90s_nyc/loc_victim_workplace/v2/scene_500k.spz',
    metersPerUnit: 1.0,
    // Without an extension this also matches the `_v2` vantage image
    // (`loc_victim_workplace_v2.jpg`), which is the same room — intended.
    imageMatch: '/images/featured/90s_nyc/loc_victim_workplace',
    // No `locationMatch`: `victim_workplace` is a generic id shared by every
    // case, so matching on it would drop other cases into this capture.
    background: 0x101010,
    antialias: true,
    splat: { quaternion: [0.705204, 0, -0.709005, 0], scale: 2.1689, position: [0.1911, 1.5778, -0.2447] },
    eyeHeight: 1.55,
    // Faces the counter and the cubbies.
    start: { pos: [0.19, -0.24], yaw: 1.5708 },
    cameraNear: 0.05,
    // One cut wall: the storefront onto the street. The garage past the shop
    // window is an overlay mesh (garage.glb), not a backdrop.
    windowBackdrop: [{
      image: '/scenes/90s_nyc/loc_victim_workplace/v2/street_wide.jpg',
      yaw: 0, distance: 3.91,
      plane: { width: 26, center: [0, 5.3125], depth: 3.39 },
      openings: [{ s0: -1.89, s1: 1.21, y0: 0.2, y1: 2.51 }],
      tint: 0xe8e8e8, softEdge: 0.01, rebuildFrames: false,
    }],
    overlays: '/scenes/90s_nyc/loc_victim_workplace/v2/overlays.json',
    collision: {
      grid: '/scenes/90s_nyc/loc_victim_workplace/v2/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-2.23, -3.92], max: [2.23, 3.72] },
    },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    examinePoints: [
      { label: 'Lockers', position: [1.466, 1.05, 2.35] },
      { label: 'Wall calendar', position: [1.976, 1.75, 0.854] },
      { label: 'Pay phone', position: [-1.845, 1.45, -2.65] },
    ],
  },
  {
    id: '90s_nyc/loc_victim_home',
    label: 'Rivera and Casey Apartment',
    url: '/scenes/90s_nyc/loc_victim_home/v2/scene_500k.spz',
    metersPerUnit: 1.0,
    // Without an extension this also matches the `_v2` vantage image
    // (`loc_victim_home_v2.jpg`), which is the same apartment — intended.
    imageMatch: '/images/featured/90s_nyc/loc_victim_home',
    // No `locationMatch`: `victim_home` is a generic id shared by every case,
    // so matching on it would drop other cases into this capture.
    background: 0x101010,
    antialias: true,
    splat: { quaternion: [0.999945, -0.001793, -0.008997, 0.005123], scale: 1.1, position: [0.5751, 1.2843, -0.2901] },
    eyeHeight: 1.55,
    // Just inside the entry, looking down the living room toward the bedroom
    // end, with the TV wall slightly right and the sofa and kitchen left.
    start: { pos: [0.2, 2.35], yaw: -0.2443 },
    cameraNear: 0.05,
    // No `windowBackdrop`: the street is six opaque overlay quads, so there is
    // nothing to cut on the glass. The walk grid blocks the bedroom.
    overlays: '/scenes/90s_nyc/loc_victim_home/v2/overlays.json',
    collision: {
      grid: '/scenes/90s_nyc/loc_victim_home/v2/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-2.0, -3.4], max: [1.9, 3.35] },
    },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    examinePoints: [
      // The bookcase's lower shelves, at least 1.3 m from the TV-shelf clue.
      { label: 'Bookshelf', position: [1.16, 0.6, -2.55] },
      { label: 'Refrigerator', position: [-0.575, 1.0, 0.095] },
      { label: 'Side table', position: [-0.97, 0.64, -2.69] },
    ],
  },
  {
    id: '90s_nyc/loc_institutional',
    label: '90th Precinct Complaint Desk',
    url: '/scenes/90s_nyc/loc_institutional/v2/scene_500k.spz',
    metersPerUnit: 1.0,
    // Without an extension this also matches the `_v2` vantage image
    // (`loc_institutional_v2.jpg`): the base image is the precinct exterior and
    // `_v2` the complaint room interior, and both map to this room — intended.
    imageMatch: '/images/featured/90s_nyc/loc_institutional',
    // No `locationMatch`: `institutional` is a generic id shared by every case,
    // so matching on it would drop other cases into this capture.
    background: 0x101010,
    antialias: true,
    splat: { quaternion: [1, 0, 0.000873, 0], scale: 2.1848, position: [-0.3513, 1.5894, 0.5669] },
    eyeHeight: 1.55,
    // Faces the partition.
    start: { pos: [-0.35, 0.57], yaw: 0 },
    cameraNear: 0.05,
    // No `windowBackdrop`: a windowless interior room.
    overlays: '/scenes/90s_nyc/loc_institutional/v2/overlays.json',
    collision: {
      grid: '/scenes/90s_nyc/loc_institutional/v2/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-2.74, -2.75], max: [2.73, 4.69] },
    },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    examinePoints: [
      { label: 'Filing cabinets', position: [-1.83, 1.0, 3.06] },
      { label: 'Precinct map', position: [-2.47, 1.95, -0.13] },
      { label: 'Notice board', position: [1.705, 1.85, 4.46] },
    ],
  },
  // The NYC crime scene is still an origin capture: camera at y = 0, scale
  // from `metersPerUnit`, no `splat`, `eyeHeight` or `start.pos`.
  {
    id: '90s_nyc/loc_crime_scene',
    label: 'Ingraham Street',
    // 7.9 MB LOD build (500k splats).
    url: '/scenes/90s_nyc/loc_crime_scene/scene_500k.spz',
    // 1.6 m eye over floorY 0.6704 (Marble 2.45; outdoors, no ceiling).
    metersPerUnit: 2.39,
    // Without an extension this also matches the `_v2` vantage image
    // (`loc_crime_scene_v2.jpg`), which is the same street — intended.
    imageMatch: '/images/featured/90s_nyc/loc_crime_scene',
    // No `locationMatch`: `crime_scene` is a generic id shared by every case,
    // so matching on it would drop other cases into this capture.
    // No `windowBackdrop` and no `background`: an outdoor street, the sky and
    // the street ends are part of the capture itself.
    // The capture smears the evidence boards, tent markers, tape and jacket
    // into unreadable blurs, so this scene carries an overlay pass: BOX cuts
    // delete the smeared originals and textured meshes take their place. The
    // file is fetched by SceneCanvas; its image paths are relative to it.
    overlays: '/scenes/90s_nyc/loc_crime_scene/overlays/overlays.json',
    collision: {
      grid: '/scenes/90s_nyc/loc_crime_scene/walkgrid.json',
      // The street has no walls at its ends and the capture fades out past
      // about 9 m behind and 11 m ahead of the origin, so a box keeps the
      // player on the sharp stretch (measured on the top-down plan render).
      bounds: { min: [-5, -9], max: [2, 11] },
    },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    examinePoints: [
      { label: 'Street debris', position: [-0.28, -0.62, -0.93] },
      { label: 'Tree pit', position: [0.49, -0.52, -3.85] },
    ],
  },
  {
    id: '90s_nyc/loc_public_place',
    label: 'Morrison Towing and Salvage',
    url: '/scenes/90s_nyc/loc_public_place/v2/scene_500k.spz',
    metersPerUnit: 1.0,
    // Without an extension this also matches the `_v2` vantage image
    // (`loc_public_place_v2.jpg`): the base image is the tow-yard exterior and
    // `_v2` the front office interior, and both map to this room — intended.
    imageMatch: '/images/featured/90s_nyc/loc_public_place',
    // No `locationMatch`: `public_place` is a generic id shared by every case,
    // so matching on it would drop other cases into this capture.
    background: 0x101010,
    antialias: true,
    splat: { quaternion: [1, 0, 0.000349, 0], scale: 1.6901, position: [0.3124, 1.5592, 0.6125] },
    eyeHeight: 1.55,
    // Faces the window and the desk.
    start: { pos: [0.31, 0.61], yaw: 0 },
    cameraNear: 0.05,
    // One cut wall: the yard window behind the desk. Side and ground quads in
    // the overlays finish the view past the backdrop's edges.
    windowBackdrop: {
      image: '/scenes/90s_nyc/loc_public_place/v2/backdrop_wide.jpg',
      yaw: 0, distance: 2.1,
      plane: { width: 24, center: [0, 5.75], depth: 6.9 },
      openings: [{ s0: -1.625, s1: 1.625, y0: 1.1, y1: 2.15 }],
      tint: 0xf8f8f8, softEdge: 0.01, rebuildFrames: false,
    },
    overlays: '/scenes/90s_nyc/loc_public_place/v2/overlays.json',
    collision: {
      grid: '/scenes/90s_nyc/loc_public_place/v2/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-2.725, -2.22], max: [2.74, 2.25] },
    },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    // The desk is left out: the office's clues lie on and in it.
    examinePoints: [
      // The tall cabinet's upper drawers, at least 1.3 m from the desk clues.
      { label: 'Filing cabinet', position: [1.70, 1.30, -1.24] },
      { label: 'Corkboard', position: [-2.437, 1.7, 0.16] },
      { label: 'Wastebasket', position: [2.31, 0.36, 1.375] },
    ],
  },
  {
    id: 'miami_vice/loc_myshaps',
    label: 'MysHaps Agency',
    // 7.7 MB LOD build (500k splats).
    url: '/scenes/miami_vice/loc_myshaps/scene_500k.spz',
    // 1.6 m eye over floorY 1.74 (Marble 0.95, the 2.8 m ceiling 0.91).
    metersPerUnit: 0.92,
    // Without an extension this also matches the `_v2` vantage image
    // (`loc_myshaps_v2.jpg`), which is the same office — intended.
    imageMatch: '/images/featured/miami_vice/loc_myshaps',
    // No `locationMatch`: `myshaps` is an id shared by every featured case, so
    // matching on it would drop other cases into this capture.
    background: 0x0c0b0e,   // night sky past the backdrop edges
    // One cut wall: the window onto Biscayne Boulevard. No `openings` and
    // `rebuildFrames: false`, so the builder draws no rails or blinds of its
    // own; the overlay pass below supplies the blinds and frame as a mesh.
    windowBackdrop: {
      image: '/scenes/miami_vice/loc_myshaps/backdrop.jpg',
      yaw: 0.982, distance: 3.405,
      plane: { width: 48, center: [6.0, -1.0], depth: 6 },
      tint: 0xe0e0e0, softEdge: 0.01, rebuildFrames: false,
    },
    // The overlay pass draws the lettered door glass as an opaque quad (so the
    // coat rack in front of it still occludes it) over a BOX cut of the smeared
    // original, and hangs a venetian-blind/frame mesh over the window, with
    // BOX cuts removing the capture's own blurred mullions and rail beneath it.
    // Baked meshes of the desk chair and ceiling fan replace their smeared
    // captures, which BOX cuts remove; the walk grid already blocks the chair.
    // Its image and mesh paths are relative to the file.
    overlays: '/scenes/miami_vice/loc_myshaps/overlays.json',
    collision: { grid: '/scenes/miami_vice/loc_myshaps/walkgrid.json' },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    examinePoints: [
      { label: 'The desk', position: [0.45, -0.75, -2.85] },
      { label: 'Filing cabinet', position: [0.45, -0.35, -4.95] },
      { label: 'Case board', position: [2.0, 0.4, -4.6] },
      { label: 'Marine chart', position: [2.87, 0.27, -3.9] },
      { label: 'Bookshelf', position: [2.2, -0.2, -1.6] },
      { label: 'Coat rack', position: [-1.3, -0.3, -0.4] },
    ],
  },
  {
    id: 'miami_vice/loc_victim_home',
    label: "Salazar's Stilt House",
    // 7.3 MB LOD build (500k splats).
    url: '/scenes/miami_vice/loc_victim_home/scene_500k.spz',
    // 1.55 m eye over floorY 0.8725 (Marble 1.81, the 2.6 m ceiling 1.76).
    metersPerUnit: 1.78,
    // Without an extension this also matches the `_v2` vantage image, which is
    // the same house — intended. No `locationMatch` (generic id).
    imageMatch: '/images/featured/miami_vice/loc_victim_home',
    background: 0x0a0b12,   // night sky past the backdrop edges
    // Painting, desk and windows: turned left far enough to frame the painting.
    start: { yaw: 0.12 },
    // One cut wall: the bay windows over the flats.
    windowBackdrop: {
      image: '/scenes/miami_vice/loc_victim_home/backdrop.jpg',
      yaw: -0.68941, distance: 1.8,
      plane: { width: 48, center: [0.0, 1.0], depth: 6 },
      tint: 0xffffff, softEdge: 0.01, rebuildFrames: false,
    },
    // 7 baked meshes and 4 quads over 47 BOX cuts of their smeared captures.
    overlays: '/scenes/miami_vice/loc_victim_home/overlays.json',
    collision: { grid: '/scenes/miami_vice/loc_victim_home/walkgrid.json' },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    // The rolltop desk and the window band above it are left out: the case's
    // letter is found in that desk.
    examinePoints: [
      // On the painting over the wicker sofa.
      { label: 'Painting', position: [-0.705, 0.12, -1.347] },
      { label: 'Kitchenette', position: [-1.48, -0.35, 0.2] },
      { label: 'Drop-leaf table', position: [-0.62, -0.44, 0.85] },
      { label: 'Sea chest', position: [0.51, -0.6, 0.76] },
    ],
  },
  {
    id: 'miami_vice/loc_victim_workplace',
    label: 'Sunwake Boat Shed',
    // 7.7 MB LOD build (500k splats).
    url: '/scenes/miami_vice/loc_victim_workplace/scene_500k.spz',
    // 1.55 m eye over floorY 0.9575 (Marble 1.60, the 4.1 m roof 1.58).
    metersPerUnit: 1.62,
    // Also matches the `_v2` vantage image (same shed) — intended.
    imageMatch: '/images/featured/miami_vice/loc_victim_workplace',
    background: 0xbbcfe9,   // daylight marina haze past the bay door
    start: { yaw: 0 },
    // One cut: the open bay door onto the marina.
    windowBackdrop: [
      {
        image: '/scenes/miami_vice/loc_victim_workplace/bd_far.jpg',
        yaw: 0.58303, distance: 1.6367,
        plane: { width: 182.26, center: [-12.43, 44.126], depth: 27.205 },
        tint: 0xffffff, softEdge: 0.01, rebuildFrames: false,
      },
    ],
    // 17 quads over 30 BOX cuts.
    overlays: '/scenes/miami_vice/loc_victim_workplace/overlays.json',
    collision: { grid: '/scenes/miami_vice/loc_victim_workplace/walkgrid.json' },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    // The test bench, its placard, the hull-plan board and the hull are left
    // out: the shed's clues and cipher point at them.
    examinePoints: [
      { label: 'Office door', position: [1.29, -0.19, -0.29] },
      // Just above the tool chest's top.
      { label: 'Tool chest', position: [1.31, -0.31, 0.33] },
      { label: 'Resin table', position: [0.21, -0.43, 0.95] },
      // On the corrugated wall at the bay door's right edge.
      { label: 'Bay door', position: [-0.287, 0, -1.751] },
    ],
  },
  {
    id: 'miami_vice/loc_institutional',
    label: 'Coast Guard Case Desk',
    // 7.4 MB LOD build (500k splats).
    url: '/scenes/miami_vice/loc_institutional/scene_500k.spz',
    // 1.55 m eye over floorY 0.867; the 2.75 m ceiling and both wall spans agree within 1%.
    metersPerUnit: 1.79,
    // Also matches the `_v2` vantage image (same station) — intended.
    imageMatch: '/images/featured/miami_vice/loc_institutional',
    background: 0xe4e2e6,   // hazy daylight past the backdrop edges
    // Turned 9° right of the desk so the corkboard is in view as well.
    start: { yaw: -0.15 },
    // Two planes on the same cut wall: the near bay photo, with a wider shot
    // of the bay behind it filling the view past its edges.
    windowBackdrop: [
      {
        image: '/scenes/miami_vice/loc_institutional/r2_bd_bay_4k.jpg',
        yaw: -1.7578, distance: 1.7578,
        plane: { width: 534.8214, center: [0.0156, 16.7524], depth: 165.8482 },
        tint: 0xffffff, softEdge: 0.01, rebuildFrames: false,
      },
      {
        image: '/scenes/miami_vice/loc_institutional/bd_bay_wide.jpg',
        yaw: -1.7578, distance: 1.7578,
        plane: { width: 1996.875, center: [0.0156, 17.8711], depth: 177.0089 },
        tint: 0xffffff, softEdge: 0.01, rebuildFrames: false,
      },
    ],
    // 171 quads and 4 meshes over 15 BOX cuts.
    overlays: '/scenes/miami_vice/loc_institutional/overlays.json',
    collision: { grid: '/scenes/miami_vice/loc_institutional/walkgrid.json' },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    // The case desk (its folder and in-trays) and the chart above it are left
    // out: the station's clue and documents lie there.
    examinePoints: [
      { label: 'Window', position: [1.56, -0.022, 0.311] },
      { label: 'Radio room', position: [-1.764, -0.196, -0.431] },
      // Just above the radio set's top.
      { label: 'Radio set', position: [0.57, -0.33, 1.45] },
      // The corkboard's left end, beside the chart.
      { label: 'Corkboard', position: [0.62, 0.05, -0.883] },
    ],
  },
  {
    id: 'miami_vice/loc_public_place',
    label: 'Blue Heron Fuel Dock',
    // 5.5 MB (353k splats after the prop cuts were baked out).
    url: '/scenes/miami_vice/loc_public_place/near.spz',
    // A metric scene: the world is the blockout's frame in metres (glTF axes,
    // floor at y = 0), so 1 unit = 1 m.
    metersPerUnit: 1.0,
    imageMatch: '/images/featured/miami_vice/loc_public_place',
    background: 0x061030,   // the sky sphere's zenith navy
    // Places the capture in the blockout frame (quaternion x, y, z, w).
    splat: { quaternion: [0.79607, 0, 0.605204, 0], scale: 2.0295, position: [0.0649, 1.55, 0.0157] },
    eyeHeight: 1.55,
    start: { pos: [0, 0], yaw: -1.3088 },
    // Props sit within a metre of the eye, so the near plane comes in from 0.1.
    cameraNear: 0.05,
    // 9 textured prop meshes over 18 BOX cuts, plus the water, sky and
    // horizon bands.
    overlays: '/scenes/miami_vice/loc_public_place/overlays.json',
    collision: {
      grid: '/scenes/miami_vice/loc_public_place/walkgrid.json',
      playerRadius: 0.25,
      // Backstop if the grid fails to load: the grid's extent (x -2.375..2.525,
      // z -1.525..1.525) plus 0.25 m. With the grid loaded it never binds.
      bounds: { min: [-2.65, -1.8], max: [2.8, 1.8] },
    },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    // The attendant's booth, the pumps and the sign over them are left out:
    // the dock's clue is the booth's pump ticket.
    examinePoints: [
      { label: 'Bar counter', position: [-0.9, 1.12, 2.15] },
      { label: 'Mooring piling', position: [-2.62, 1.08, -0.45] },
      { label: 'Moored boat', position: [0.55, 0.6, -4.07] },
      // Just above the red locker's top, beside the booth.
      { label: 'Red locker', position: [2.68, 1.92, -1.74] },
    ],
  },
  {
    id: 'miami_vice/loc_crime_scene',
    label: 'Pelican Reach Marina, Slip 14',
    // 7.0 MB (441k splats).
    url: '/scenes/miami_vice/loc_crime_scene/near.spz',
    // A metric scene, as the fuel dock: 1 unit = 1 m.
    metersPerUnit: 1.0,
    imageMatch: '/images/featured/miami_vice/loc_crime_scene',
    background: 0x31304a,   // dusk sky past the capture
    splat: { quaternion: [0.999966, 0, 0.008247, 0], scale: 2.5178, position: [2.7, 1.55, 0.75] },
    eyeHeight: 1.55,
    start: { pos: [2.7, 0.75], yaw: 0 },
    cameraNear: 0.05,
    // 20 textured prop meshes over 32 BOX cuts, plus the water.
    overlays: '/scenes/miami_vice/loc_crime_scene/overlays.json',
    collision: {
      grid: '/scenes/miami_vice/loc_crime_scene/walkgrid.json',
      playerRadius: 0.25,
      // Backstop if the grid fails to load: the grid's extent (x 1.9..7.8,
      // z -4.1..4.0) plus 0.25 m. With the grid loaded it never binds.
      bounds: { min: [1.65, -4.35], max: [8.05, 4.25] },
    },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    // The boat's cockpit and the dock office booth are left out: the slip's
    // clues are in the cockpit scupper and on the booth wall.
    examinePoints: [
      // Just above the pedestal's top.
      { label: 'Power pedestal', position: [4.35, 1.42, 0.87] },
      { label: 'Dock box', position: [3.88, 0.7, 3.29] },
      // Just above the left finger-pier piling's top.
      { label: 'Finger-pier piling', position: [2.3, 1.52, -3.65] },
    ],
  },
  // The noir case: six metric rooms (1 unit = 1 m, floor at y = 0), shown in
  // black and white (`grayscale`) with MSAA on (`antialias`). The agency and
  // the front office are splat plates with prop meshes; the screening room,
  // the film exchange and the study are mesh-only (no `url`, no `splat`) and
  // draw from `overlays`; the Astor is a baked mesh room loaded from
  // `meshRoom`. Values mirror each room's archive viewer room.json. The
  // collision bounds are the viewer's room bounds; the grid (origin, cells)
  // lives in walkgrid.json.
  {
    id: 'noir/loc_myshaps',
    label: 'MysHaps Agency',
    url: '/scenes/noir/loc_myshaps/scene_500k.spz',
    metersPerUnit: 1.0,
    imageMatch: '/images/featured/noir/loc_myshaps',
    background: 0x0b0b0b,
    grayscale: true,
    antialias: true,
    splat: { quaternion: [0.99996, 0, 0.008901, 0], scale: 1.5576, position: [0.0847, 1.6549, 1.1007] },
    eyeHeight: 1.55,
    start: { pos: [0.147, 1.145], yaw: 0 },
    cameraNear: 0.05,
    windowBackdrop: {
      image: '/scenes/noir/loc_myshaps/backdrop_wide.jpg',
      yaw: 0, distance: 3.4,
      plane: { width: 12.0, center: [1.4, 2.075], depth: 2.9 },
      openings: [{ s0: -0.07, s1: 1.13, y0: 0.9, y1: 2.27 }],
      tint: 0xd8d8d8, softEdge: 0.01, rebuildFrames: false,
      ground: { y: -1.3, color: 0x7c7c7c },
    },
    overlays: '/scenes/noir/loc_myshaps/overlays.json',
    collision: {
      grid: '/scenes/noir/loc_myshaps/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-2.75, -3.45], max: [2.75, 2.65] },
    },
    // Neutral spots that open the Examine window; every room has them, so they
    // reveal nothing about where clues are. World coordinates of the scene.
    examinePoints: [
      { label: 'Filing cabinets', position: [-1.04, 1.1, -2.43] },
      { label: 'Coffee stand', position: [1.06, 1.06, -2.83] },
      { label: 'Window', position: [0.55, 1.5, -3.25] },
      { label: 'Bookcase', position: [0.535, 1.2, 2.03] },
    ],
  },
  {
    id: 'noir/loc_victim_workplace',
    label: 'Meridian Front Office',
    url: '/scenes/noir/loc_victim_workplace/scene_500k.spz',
    metersPerUnit: 1.0,
    imageMatch: '/images/featured/noir/loc_victim_workplace',
    background: 0x0b0b0b,
    grayscale: true,
    antialias: true,
    splat: { quaternion: [0.999938, 0, 0.011126, 0], scale: 1.9882, position: [0.9518, 1.55, -0.1556] },
    eyeHeight: 1.55,
    start: { pos: [0.9, -0.1], yaw: 0 },
    cameraNear: 0.05,
    windowBackdrop: {
      image: '/scenes/noir/loc_victim_workplace/backdrop_street.jpg',
      yaw: 1.570796, distance: 3.09,
      plane: { width: 24.0, center: [0.45, 1.75], depth: 2.71 },
      openings: [{ s0: -0.2, s1: 1.08, y0: 0.89, y1: 2.78 }],
      tint: 0xcfcfcf, softEdge: 0.01, rebuildFrames: false,
    },
    overlays: '/scenes/noir/loc_victim_workplace/overlays.json',
    collision: {
      grid: '/scenes/noir/loc_victim_workplace/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-3.25, -3.75], max: [4.55, 2.85] },
    },
    // Both desks (the out-tray and the open ledger) are left out: the office's
    // clues lie there.
    examinePoints: [
      { label: 'Production board', position: [0.27, 1.7, -2.37] },
      { label: 'Filing cabinets', position: [-1.2, 1.1, -1.69] },
      { label: 'Coat tree', position: [-2.68, 1.3, -1.55] },
      // Just above the typewriter's top.
      { label: 'Typewriter', position: [-1.35, 1.07, -0.64] },
    ],
  },
  {
    id: 'noir/loc_crime_scene',
    label: 'Stage 4 Screening Room',
    metersPerUnit: 1.0,
    imageMatch: '/images/featured/noir/loc_crime_scene',
    background: 0x0b0b0b,
    grayscale: true,
    antialias: true,
    eyeHeight: 1.55,
    start: { pos: [-0.175, -0.82], yaw: 0.7854 },
    cameraNear: 0.05,
    windowBackdrop: {
      image: '/scenes/noir/loc_crime_scene/backdrop_hall.jpg',
      yaw: 0, distance: 2.86,
      plane: { width: 32.9697, center: [-0.22, -0.7282], depth: 6.075 },
      openings: [{ s0: -0.575, s1: 0.1575, y0: 1.145, y1: 1.745 }],
      tint: 0xffffff, softEdge: 0.01, rebuildFrames: false,
    },
    overlays: '/scenes/noir/loc_crime_scene/overlays.json',
    collision: {
      grid: '/scenes/noir/loc_crime_scene/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-2.75, -2.995], max: [1.645, -0.025] },
    },
    // The rewind bench, the booth door and the strongbox are left out: the
    // booth's clues and documents lie there.
    examinePoints: [
      { label: 'Projector', position: [-0.85, 1.45, -1.7] },
      { label: 'Film rack', position: [0.72, 1.2, -0.5] },
      { label: 'Electrical panel', position: [1.3, 1.5, -1.1] },
    ],
  },
  {
    id: 'noir/loc_institutional',
    label: 'Consolidated Film Exchange',
    metersPerUnit: 1.0,
    imageMatch: '/images/featured/noir/loc_institutional',
    background: 0x0b0b0b,
    grayscale: true,
    antialias: true,
    eyeHeight: 1.55,
    start: { pos: [0.9, 1.2], yaw: 0.5236 },
    cameraNear: 0.05,
    overlays: '/scenes/noir/loc_institutional/overlays.json',
    collision: {
      grid: '/scenes/noir/loc_institutional/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-3.85, -3.45], max: [3.85, 2.85] },
    },
    // The counter and the dolly are left out: the exchange's document lies
    // there.
    examinePoints: [
      { label: 'Storage cage', position: [0.35, 1.3, -1.74] },
      { label: 'Pigeonholes', position: [-3.16, 1.3, -0.5] },
      { label: 'Platform scale', position: [3.18, 1.34, 0.45] },
    ],
  },
  {
    id: 'noir/loc_victim_home',
    label: "Whitmore's Study",
    metersPerUnit: 1.0,
    imageMatch: '/images/featured/noir/loc_victim_home',
    background: 0x0b0b0b,
    grayscale: true,
    antialias: true,
    eyeHeight: 1.55,
    start: { pos: [0.6, 2.4], yaw: 0 },
    cameraNear: 0.05,
    // Room meshes and props, plus the sky and the hillside vista.
    overlays: '/scenes/noir/loc_victim_home/overlays.json',
    collision: {
      grid: '/scenes/noir/loc_victim_home/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-3.15, -3.75], max: [3.15, 2.95] },
    },
    // The desk and the phone are left out: the study's clue lies there.
    examinePoints: [
      // The iron lantern hung from the ceiling.
      { label: 'Lantern', position: [0, 2.7, -0.35] },
      { label: 'Bookshelves', position: [-1.0, 1.6, -3.12] },
      { label: 'Club chair', position: [-1.3, 0.9, -0.35] },
      { label: 'Window', position: [2.95, 1.5, -0.4] },
    ],
  },
  {
    id: 'noir/loc_public_place',
    label: 'The Astor Theatre, Broadway',
    metersPerUnit: 1.0,
    imageMatch: '/images/featured/noir/loc_public_place',
    background: 0xc4c7cb,   // overcast sky past the street
    grayscale: true,
    antialias: true,
    eyeHeight: 1.55,
    start: { pos: [2.6, 3.4], yaw: 0.698132 },
    cameraNear: 0.05,
    // A baked Blender room (mesh, lightmaps, lighting) in place of overlays.
    meshRoom: '/scenes/noir/loc_public_place/room.json',
    collision: {
      grid: '/scenes/noir/loc_public_place/walkgrid.json',
      playerRadius: 0.25,
      bounds: { min: [-2.5, -11.5], max: [5.3, 10.0] },
    },
    // The kiosk, the box office, the posters and the marquee are left out: the
    // theatre's clue lies there.
    examinePoints: [
      { label: 'Vestibule lantern', position: [-1.08, 3.0, 1.5] },
      { label: 'Newsstand', position: [2.9, 1.3, 6.68] },
      { label: 'Cellar hatch', position: [0.95, 0.85, 6.45] },
    ],
  },
];

// Resolved once so every `resolveScene` call hands back the same objects.
const SCENES = AUTHORED_SCENES.map((scene) => withSceneAssetBase(scene));

/**
 * Resolve the splat scene for the current location, or null when the location
 * has no 3D capture and the 2D image should be used instead.
 *
 * @param {{ currentLocation?: string, currentImage?: string }} params
 * @returns {{
 *   id: string,
 *   label: string,
 *   url?: string,
 *   meshRoom?: string,
 *   grayscale?: boolean,
 *   antialias?: boolean,
 *   metersPerUnit?: number,
 *   background?: number,
 *   windowBackdrop?: object | object[],
 *   overlays?: string,
 *   collision?: {
 *     grid?: string,
 *     playerRadius?: number,
 *     bounds?: { min: [number, number], max: [number, number] },
 *     blockers?: { center: [number, number], size: [number, number], yaw?: number }[],
 *   },
 *   examinePoints?: { label: string, position: [number, number, number] }[],
 *   start?: { yaw?: number, pos?: [number, number] },
 *   splat?: {
 *     quaternion?: [number, number, number, number],
 *     scale?: number,
 *     position?: [number, number, number],
 *   },
 *   eyeHeight?: number,
 *   cameraNear?: number,
 * } | null}
 */
export const resolveScene = ({ currentLocation, currentImage } = {}) => {
  const image = typeof currentImage === 'string' ? currentImage : '';
  const locationId = typeof currentLocation === 'string' ? currentLocation.toLowerCase() : '';

  return SCENES.find((scene) => (
    (scene.imageMatch && image.includes(scene.imageMatch)) ||
    (scene.locationMatch && locationId.includes(scene.locationMatch))
  )) || null;
};

/**
 * Whether any location in a case roster has a 3D scene. Same matching as
 * `resolveScene`, applied to each location's own id and image.
 *
 * @param {{ id?: string, image?: string }[]} locations
 * @returns {boolean}
 */
export const caseHasScenes = (locations) => (
  Array.isArray(locations) && locations.some((location) => (
    Boolean(location) && resolveScene({ currentLocation: location.id, currentImage: location.image }) !== null
  ))
);

/**
 * Start heading for a resolved scene: `start.yaw` in radians when it is a
 * finite number, otherwise 0 (face -z).
 *
 * @param {{ start?: { yaw?: number } } | null | undefined} scene
 * @returns {number}
 */
export const resolveStartYaw = (scene) => {
  const yaw = scene?.start?.yaw;
  return Number.isFinite(yaw) ? yaw : 0;
};

export default SCENES;
